import React, { useEffect, useState } from 'react';
import { WorkoutExercise } from '../../../types';
import styles from './EditWorkoutExercise.module.css';
import ConfirmationModal from '../../common/ConfirmationModal';
import { useTranslation } from '../../../i18n/provider';

interface EditWorkoutExerciseProps {
  exercise: WorkoutExercise;
  onBack: () => void;
  onSave: (updated: WorkoutExercise) => void;
  onNavigateToExercise?: (exerciseId: string, templateId?: string) => void;
}

interface SetEntry {
  id: string;
  reps: number;
  weight: number;
  completed: boolean;
}

const EditWorkoutExercise: React.FC<EditWorkoutExerciseProps> = ({ exercise, onBack, onSave, onNavigateToExercise }) => {
  const { t } = useTranslation();
  const [sets, setSets] = useState<SetEntry[]>([]);
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);

  // TODO.md E, item 5: stretching/mobility exercises collect a single hold-time value (seconds)
  // instead of separate reps/weight inputs. The value is stored in the existing `reps` field with
  // `unit: 'sec'` - no schema change (see the decision note in TODO.md and gym-planner-backend/src/types.ts).
  const isHoldTime = exercise.exerciseType === 'stretching' || exercise.exerciseType === 'mobility';

  // Initialize sets from the workout exercise aggregate data
  useEffect(() => {
    console.log('🔍 [EDIT EXERCISE] Exercise prop received:', {
      name: exercise.name,
      id: exercise.id,
      sets: exercise.sets,
      completedSets: exercise.completedSets,
      hasSetsData: !!exercise.setsData,
      setsData: exercise.setsData,
      setsDataLength: exercise.setsData?.length || 0
    });
    
    const count = Math.max(1, exercise.sets);
    let initial: SetEntry[] = [];
    
    // If we have setsData, use it to preserve per-set values
    if (exercise.setsData && Array.isArray(exercise.setsData) && exercise.setsData.length > 0) {
      console.log('🔄 [EDIT EXERCISE] Initializing from setsData:', exercise.setsData);
      initial = exercise.setsData.map((setData, idx) => ({
        id: `${exercise.id}-set-${idx + 1}`,
        reps: setData.reps,
        weight: setData.weight,
        completed: true, // All sets in setsData are considered completed
      }));
      
      // Add remaining empty sets if needed
      const remainingSets = count - initial.length;
      if (remainingSets > 0) {
        const emptySets = Array.from({ length: remainingSets }).map((_, idx) => ({
          id: `${exercise.id}-set-${initial.length + idx + 1}`,
          reps: exercise.reps,
          weight: exercise.weight,
          completed: false,
        }));
        initial = [...initial, ...emptySets];
      }
    } else {
      // Fallback to aggregate data
      console.log('🔄 [EDIT EXERCISE] Initializing from aggregate data');
      initial = Array.from({ length: count }).map((_, idx) => ({
        id: `${exercise.id}-set-${idx + 1}`,
        reps: exercise.reps,
        weight: exercise.weight,
        completed: idx < exercise.completedSets,
      }));
    }
    
    console.log('✅ [EDIT EXERCISE] Initial sets array:', initial);
    setSets(initial);
  }, [exercise]);

  const addSet = () => {
    setSets(prev => ([
      ...prev,
      { id: `${exercise.id}-set-${prev.length + 1}`, reps: exercise.reps, weight: isHoldTime ? 0 : exercise.weight, completed: false }
    ]));
  };

  const updateSetField = (id: string, field: 'reps' | 'weight', value: number) => {
    setSets(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const removeSet = (id: string) => {
    setSets(prev => prev.filter(s => s.id !== id));
  };

  const toggleSetCompleted = (id: string, checked: boolean) => {
    setSets(prev => prev.map(s => (s.id === id ? { ...s, completed: checked } : s)));
  };

  const handleSave = () => {
    const setsCount = sets.length;
    const last = sets[setsCount - 1] || { 
      reps: Number(exercise.reps) || 0, 
      weight: Number(exercise.weight) || 0 
    };
    const completedSets = sets.filter(s => s.completed).length;
    const isCompleted = setsCount > 0 && completedSets === setsCount;
    
    console.log('💾 [EDIT EXERCISE] Save debug:', {
      exerciseName: exercise.name,
      totalSets: setsCount,
      completedSets,
      allSets: sets.map(s => ({ id: s.id, completed: s.completed, reps: s.reps, weight: s.weight })),
      isCompleted
    });
    
    // Warn if trying to save without completing any sets
    if (setsCount > 0 && completedSets === 0) {
      setShowIncompleteWarning(true);
      return;
    }
    
    // Create array of completed set objects for workout history
    // Hold-time sets (TODO.md E, item 5) always save unit: 'sec' with weight forced to 0, regardless
    // of what exercise.unit happens to already be (e.g. a stale 'kg' default from before exerciseType
    // was known) - the exercise's type, not its current unit field, is the source of truth here.
    const completedSetData = sets
      .filter(s => s.completed)
      .map(s => ({
        reps: Number(s.reps) || 0,
        weight: isHoldTime ? 0 : (Number(s.weight) || 0),
        unit: isHoldTime ? 'sec' : (exercise.unit || 'kg')
      }));

    console.log('📊 [EDIT EXERCISE] Completed set data:', completedSetData);

    const updated: WorkoutExercise = {
      ...exercise,
      sets: setsCount, // Keep original sets count
      setsData: completedSetData.length > 0 ? completedSetData : undefined, // Store array in separate property
      reps: Number(last.reps) || 0,
      weight: isHoldTime ? 0 : (Number(last.weight) || 0),
      unit: isHoldTime ? 'sec' : exercise.unit,
      completed: isCompleted,
      completedSets,
    };
    
    console.log('✅ [EDIT EXERCISE] Updated exercise:', {
      name: updated.name,
      sets: updated.sets,
      completedSets: updated.completedSets,
      setsData: updated.setsData,
      completed: updated.completed,
      weight: updated.weight,
      weightType: typeof updated.weight
    });
    
    onSave(updated);
  };

  const handleIncompleteWarningConfirm = () => {
    setShowIncompleteWarning(false);
    
    // Save without completed sets (user confirmed)
    const setsCount = sets.length;
    const last = sets[setsCount - 1] || { 
      reps: Number(exercise.reps) || 0, 
      weight: Number(exercise.weight) || 0 
    };
    
    const updated: WorkoutExercise = {
      ...exercise,
      sets: setsCount,
      reps: Number(last.reps) || 0,
      weight: isHoldTime ? 0 : (Number(last.weight) || 0),
      unit: isHoldTime ? 'sec' : exercise.unit,
      completed: false,
      completedSets: 0,
    };

    console.log('✅ [EDIT EXERCISE] Saved without completed sets');
    onSave(updated);
  };

  const handleIncompleteWarningCancel = () => {
    setShowIncompleteWarning(false);
    console.log('❌ [EDIT EXERCISE] Save cancelled - user chose to complete sets');
  };

  return (
    <div className="workout-content workout-exercise-detail">
      <div className="page-content">
        <div className="content-card">
          <div className={styles.headerRow}>
            <h2 className="content-title">{exercise.name}</h2>
          </div>

          <div className={styles.detailsRow}>
            <div className={styles.exerciseImage}>
              {/* externalUrl is dead here - the backend stopped returning it after migration 15
                  merged it into image_url (TODO.md B3/F4); imageUrl is the only field populated now. */}
              {exercise.imageUrl ? (
                <img
                  src={exercise.imageUrl}
                  alt={`${exercise.name} preview`}
                  className={styles.exerciseImageImg}
                  loading="lazy"
                />
              ) : (
                <div className={styles.exerciseImagePlaceholder}>
                  {t('shared.common.noImage')}
                </div>
              )}
              {onNavigateToExercise && (
                <button
                  className={styles.infoButton}
                  onClick={() => onNavigateToExercise(exercise.id)}
                  title={t('shared.common.viewExerciseDetails')}
                  aria-label={t('shared.common.viewExerciseInformation')}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </button>
              )}
            </div>
            <div className={styles.exerciseMeta}>
              <div>{t(`muscleGroups.${exercise.muscleGroup}`, { defaultValue: exercise.muscleGroup.toUpperCase() })}</div>
              <div>{t('workout.editExercise.setsLabel', { count: exercise.sets })}</div>
              <div>{t('workout.editExercise.completedSetsLabel', { count: exercise.completedSets })}</div>
              {isHoldTime ? (
                <div>{t('workout.editExercise.holdTimeMetaLabel', { count: exercise.reps })}</div>
              ) : (
                <>
                  <div>{t('workout.editExercise.repsLabel', { count: exercise.reps })}</div>
                  <div>{t('workout.editExercise.weightLabel', { weight: exercise.weight, unit: exercise.unit })}</div>
                </>
              )}
            </div>
          </div>

          <div className={styles.setsSection}>
            <h3 className={styles.sectionTitle}>{t('workout.editExercise.sectionTitle')}</h3>
            <div className={styles.setsList}>
              {sets
                .filter((s, index, arr) => {
                  // Show all completed sets + first incomplete set
                  if (s.completed) {
                    return true;
                  }
                  // For incomplete sets, only show the first one
                  const firstIncompleteIndex = arr.findIndex(set => !set.completed);
                  return index === firstIncompleteIndex;
                })
                .map((s, index) => (
                  <div key={s.id} className={styles.setRow}>
                    <div className={styles.setIndex}>#{index + 1}</div>
                    <div className={styles.setRowContent}>
                      <div className={styles.setRowTop}>
                        <div className={styles.checkboxWrapper}>
                          <input
                            type="checkbox"
                            checked={s.completed}
                            onChange={(e) => toggleSetCompleted(s.id, e.target.checked)}
                            id={`set-${s.id}-completed`}
                          />
                          <label htmlFor={`set-${s.id}-completed`}>
                            {t('workout.editExercise.completed')}
                          </label>
                        </div>
                      </div>
                      <div className={styles.setRowBottom}>
                        <div className={styles.setRowLeft}>
                          <div className={styles.fieldRow}>
                            {isHoldTime ? (
                              // TODO.md E, item 5: stretching/mobility exercises collect a single hold
                              // time (seconds) instead of separate reps/weight inputs. The value is
                              // stored in the existing `reps` field with unit 'sec'; weight is not
                              // collected at all here (forced to 0 in handleSave/addSet).
                              <input
                                type="number"
                                min={0}
                                value={s.reps}
                                onChange={(e) => updateSetField(s.id, 'reps', Number(e.target.value))}
                                className={styles.inputNumberMedium}
                                disabled={s.completed}
                                placeholder={t('workout.editExercise.holdTimePlaceholder')}
                                aria-label={t('workout.editExercise.holdTimeFieldLabel')}
                                data-testid={`set-${s.id}-hold-time`}
                              />
                            ) : (
                              <>
                                <input
                                  type="number"
                                  min={0}
                                  value={s.reps}
                                  onChange={(e) => updateSetField(s.id, 'reps', Number(e.target.value))}
                                  className={styles.inputNumberSmall}
                                  disabled={s.completed}
                                  placeholder={t('workout.editExercise.repsPlaceholder')}
                                />
                                <span style={{ opacity: 0.5 }}>×</span>
                                <input
                                  type="number"
                                  min={0}
                                  value={s.weight}
                                  onChange={(e) => updateSetField(s.id, 'weight', Number(e.target.value))}
                                  className={styles.inputNumberMedium}
                                  disabled={s.completed}
                                  placeholder={t('workout.editExercise.weightPlaceholder')}
                                />
                              </>
                            )}
                            <span className={styles.unitText}>{isHoldTime ? t('workout.editExercise.holdTimeUnitShort') : exercise.unit}</span>
                          </div>
                        </div>
                        <div className={styles.setRowRight}>
                          {!s.completed && (
                            <button
                              className={styles.removeButton}
                              onClick={() => removeSet(s.id)}
                              title={t('workout.editExercise.removeSet')}
                            >
                              {t('workout.editExercise.remove')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
      </div>

      {sets.every(s => s.completed) && (
        <div className={styles.addSetRow}>
          <button className="button button--secondary" onClick={addSet}>+ {t('workout.editExercise.addSet')}</button>
        </div>
      )}

          <div className={styles.actionsRow}>
            <button className="button button--secondary" onClick={onBack}>{t('workout.today.cancel')}</button>
            <button className="button button--danger" onClick={handleSave}>{t('workout.history.save')}</button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showIncompleteWarning}
        title={t('workout.editExercise.noCompletedTitle')}
        message={t('workout.editExercise.noCompletedMessage', { name: exercise.name })}
        confirmText={t('workout.editExercise.saveAnyway')}
        cancelText={t('workout.editExercise.goBack')}
        onConfirm={handleIncompleteWarningConfirm}
        onCancel={handleIncompleteWarningCancel}
        isDangerous={false}
      />
    </div>
  );
};

export default EditWorkoutExercise;